import { useEffect, useGlobals } from '@storybook/addons';
import { DecoratorFn } from '@storybook/react';
import { ColorSchemeIdentifier, colorSchemeIdentifiers, PreferredColorSchemeProvider } from './color-scheme';
import { Theme, ThemeProvider } from './theme';
import { boxcryptorTheme, msTeamsTheme } from './themes';

const themeConfiguration = { boxcryptor: boxcryptorTheme, msTeams: msTeamsTheme };
type ThemeName = keyof typeof themeConfiguration;

function getBackgroundColorName(name: ThemeName, colorScheme: ColorSchemeIdentifier) {
  return `${name}-${colorScheme}`;
}
function createBackgroundColors(name: ThemeName, theme: Theme) {
  return colorSchemeIdentifiers.map((it) => ({ name: getBackgroundColorName(name, it), value: theme[it].body }));
}
const backgroundColors = Object.entries(themeConfiguration)
  .map(([name, theme]) => createBackgroundColors(name as ThemeName, theme))
  .flat();

const Decorator: DecoratorFn = (Story) => {
  const [globals, updateGlobals] = useGlobals();
  const themeName: ThemeName | undefined = globals['theme'];
  if (!themeName) {
    throw new Error(`Missing globals['theme']`);
  }
  const colorScheme: ColorSchemeIdentifier | undefined = globals['colorScheme'];
  if (!colorScheme) {
    throw new Error(`Missing globals['colorScheme']`);
  }
  const theme = themeConfiguration[themeName];

  useEffect(() => {
    // { name: 'teams dark ios', value: '#292828' },
    updateGlobals({ backgrounds: { value: theme[colorScheme].body } });
  }, [themeName, colorScheme, updateGlobals, theme]);

  return (
    <PreferredColorSchemeProvider value={colorScheme}>
      <div className={colorScheme}>
        <ThemeProvider theme={theme}>
          <Story />
        </ThemeProvider>
      </div>
    </PreferredColorSchemeProvider>
  );
};

type Options = { defaultTheme?: ThemeName };
export function createStorybookThemePreview(options: Options = {}) {
  const defaultTheme = options.defaultTheme ?? 'boxcryptor';
  const defaultColorScheme: ColorSchemeIdentifier = 'light';
  const globalTypes = {
    theme: {
      name: 'theme',
      description: 'Theme',
      defaultValue: defaultTheme,
      toolbar: {
        icon: 'paintbrush',
        items: Object.keys(themeConfiguration),
        showName: true,
      },
    },
    colorScheme: {
      name: 'colorScheme',
      description: 'ColorScheme',
      defaultValue: defaultColorScheme,
      toolbar: {
        icon: 'lightning',
        items: colorSchemeIdentifiers,
        showName: true,
      },
    },
  };

  const backgrounds = {
    default: getBackgroundColorName(defaultTheme, defaultColorScheme),
    values: backgroundColors,
  };
  const decorators = [Decorator];

  return { globalTypes, decorators, backgrounds };
}
